import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp,
  CreditCard,
  PiggyBank,
  BookOpen,
  Target,
  Calculator,
  BarChart3,
  GraduationCap,
  ArrowRight,
  Sparkles,
  User,
  RefreshCw,
  ChevronRight,
  Brain,
  LineChart,
  Wallet,
  FileText,
  Clock,
  Gift,
  Wrench,
  Newspaper
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import SignInOverlay from "@/components/SignInOverlay";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";
import AIFinancialCoach from "@/components/AIFinancialCoach";
import Footer from "@/components/Footer";

// Category data matching CategoryQuiz.jsx
const CATEGORY_INFO = {
  debtFree: {
    name: "Get Debt-Free",
    path: "/debt-free",
    icon: CreditCard,
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    textColor: "text-red-600",
    description: "Focus on eliminating debt and taking control of your finances"
  },
  startInvesting: {
    name: "Start Investing",
    path: "/start-investing",
    icon: TrendingUp,
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    textColor: "text-emerald-600",
    description: "Begin your investment journey with confidence"
  },
  buildWealth: {
    name: "Build Wealth",
    path: "/build-wealth",
    icon: PiggyBank,
    color: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    textColor: "text-amber-600",
    description: "Optimize and grow your existing investments"
  },
  learnFinance: {
    name: "Learn Finance",
    path: "/learn-finance",
    icon: BookOpen,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-600",
    description: "Build your financial knowledge foundation"
  },
  planSave: {
    name: "Plan & Save",
    path: "/plan-save",
    icon: Target,
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-600",
    description: "Budget and save for your specific goals"
  }
};

// Investor type data
const INVESTOR_TYPES = {
  builder: { name: "The Strategic Builder", emoji: "🏗️", color: "bg-blue-500" },
  optimizer: { name: "The Balanced Optimizer", emoji: "⚖️", color: "bg-emerald-500" },
  visionary: { name: "The Bold Visionary", emoji: "🚀", color: "bg-purple-500" },
  climber: { name: "The Steady Climber", emoji: "🧗", color: "bg-amber-500" },
  learner: { name: "The Future-Ready Learner", emoji: "📚", color: "bg-pink-500" },
  opportunist: { name: "The Market Opportunist", emoji: "🎯", color: "bg-red-500" },
  protector: { name: "The Wealth Protector", emoji: "🛡️", color: "bg-slate-500" },
  maverick: { name: "The Trend Maverick", emoji: "⚡", color: "bg-orange-500" }
};

// AI Tools for dashboard (excluding AI Coach which is a floating widget)
const AI_TOOLS = [
  {
    name: "Stock Market Simulator",
    description: "Practice investing with virtual money",
    icon: LineChart,
    path: "/tools?tool=simulator",
    color: "bg-blue-500"
  },
  {
    name: "Document Analyzer",
    description: "AI analysis of your financial documents",
    icon: FileText,
    path: "/tools?tool=document-analyzer",
    color: "bg-purple-500"
  },
  {
    name: "Net Worth Calculator",
    description: "Track your total financial picture",
    icon: Wallet,
    path: "/tools?tool=networth",
    color: "bg-amber-500"
  }
];

// Suggested categories based on primary recommendation
const getSuggestedCategories = (primaryCategory) => {
  const suggestions = {
    debtFree: ["planSave", "learnFinance"],
    startInvesting: ["learnFinance", "buildWealth"],
    buildWealth: ["startInvesting", "planSave"],
    learnFinance: ["startInvesting", "planSave"],
    planSave: ["debtFree", "startInvesting"]
  };
  return suggestions[primaryCategory] || ["startInvesting", "learnFinance"];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [investorType, setInvestorType] = useState(null);
  const [recommendedCategory, setRecommendedCategory] = useState(null);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  const [aiCoachOpen, setAiCoachOpen] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    // Initialize from localStorage immediately
    const onboardingComplete = localStorage.getItem("onboardingComplete");
    const storedCategory = localStorage.getItem("recommendedCategory");
    return onboardingComplete === "true" || !!storedCategory;
  });

  useEffect(() => {
    const loadUserData = async () => {
      // Get user data
      const userData = localStorage.getItem("user_data") || sessionStorage.getItem("user_data");
      let currentUser = null;
      if (userData) {
        try {
          currentUser = JSON.parse(userData);
          setUser(currentUser);
        } catch (e) {
          console.error("Failed to parse user data");
        }
      }

      // First check localStorage for quiz data
      let storedInvestorType = localStorage.getItem("investorQuizCompleted");
      let storedCategory = localStorage.getItem("recommendedCategory");
      let onboardingComplete = localStorage.getItem("onboardingComplete");

      // If localStorage is empty but user is logged in, fetch from database
      if (currentUser && (!storedInvestorType || !storedCategory)) {
        try {
          const userId = currentUser.email || currentUser.id;
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/progress/${userId}`);
          if (response.ok) {
            const progress = await response.json();
            
            // If onboarding data exists in database, restore to localStorage
            if (progress.onboarding && progress.onboarding.investor_type) {
              storedInvestorType = progress.onboarding.investor_type;
              storedCategory = progress.onboarding.recommended_category;
              
              // Restore to localStorage for future use
              localStorage.setItem("investorQuizCompleted", storedInvestorType);
              localStorage.setItem("recommendedCategory", storedCategory);
              localStorage.setItem("onboardingComplete", "true");
              onboardingComplete = "true";
              
              console.log("Restored onboarding data from database");
            }
          }
        } catch (error) {
          console.error("Error fetching user progress:", error);
        }
      }

      // Set investor type
      if (storedInvestorType) {
        const typeKey = Object.keys(INVESTOR_TYPES).find(
          key => INVESTOR_TYPES[key].name === storedInvestorType
        );
        setInvestorType(typeKey || "learner");
      }

      // Set recommended category
      if (storedCategory) {
        setRecommendedCategory(storedCategory);
        setHasCompletedOnboarding(true);
      }

      // Check if onboarding was completed
      if (onboardingComplete === "true") {
        setHasCompletedOnboarding(true);
      }

      setIsLoadingProgress(false);
    };

    loadUserData();
  }, []);

  // If user hasn't completed onboarding, redirect to onboarding
  useEffect(() => {
    if (!isLoadingProgress && !hasCompletedOnboarding && !recommendedCategory) {
      // Check if they at least took the investor quiz
      const investorQuizDone = localStorage.getItem("investorQuizCompleted");
      if (!investorQuizDone) {
        navigate("/onboarding");
      }
    }
  }, [isLoadingProgress, hasCompletedOnboarding, recommendedCategory, navigate]);

  const categoryInfo = recommendedCategory ? CATEGORY_INFO[recommendedCategory] : CATEGORY_INFO.startInvesting;
  const CategoryIcon = categoryInfo?.icon || TrendingUp;
  const suggestedCategories = getSuggestedCategories(recommendedCategory || "startInvesting");
  const investorData = investorType ? INVESTOR_TYPES[investorType] : INVESTOR_TYPES.learner;

  const handleRetakeQuiz = () => {
    localStorage.removeItem("investorQuizCompleted");
    localStorage.removeItem("recommendedCategory");
    localStorage.removeItem("onboardingComplete");
    navigate("/onboarding");
  };

  // Show loading state while fetching progress
  if (isLoadingProgress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <SignInOverlay message="Sign in to access your personalized dashboard">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" data-testid="dashboard-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700" data-testid="dashboard-title">
                  Your Dashboard
                </h1>
                <p className="text-slate-600 mt-1">
                  Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! Here's your personalized financial journey.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRetakeQuiz}
                className="shrink-0"
                data-testid="retake-quiz-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake Quiz
              </Button>
            </div>
          </motion.div>

          {/* Investor Type Badge */}
          {investorData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              <Card className="border-0 shadow-sm bg-white/80 backdrop-blur">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${investorData.color} rounded-xl flex items-center justify-center`}>
                      <span className="text-2xl">{investorData.emoji}</span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Your Investor Type</p>
                      <p className="font-heading font-bold text-navy-700">{investorData.name}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Recommended Category */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <Card className={`border-2 ${categoryInfo?.borderColor || 'border-emerald-200'} overflow-hidden`} data-testid="recommended-category-card">
                <div className={`bg-gradient-to-r ${categoryInfo?.color || 'from-emerald-500 to-emerald-600'} p-6 text-white`}>
                  <Badge className="bg-white/20 text-white border-0 mb-3">
                    Recommended For You
                  </Badge>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <CategoryIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-heading font-bold">{categoryInfo?.name || 'Start Investing'}</h2>
                      <p className="text-white/80 text-sm">{categoryInfo?.description}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-4">
                    Based on your quiz results, this is the best starting point for your financial journey.
                    Focus here first before moving to other areas.
                  </p>
                  <Link to={categoryInfo?.path || '/start-investing'}>
                    <Button className={`w-full bg-gradient-to-r ${categoryInfo?.color || 'from-emerald-500 to-emerald-600'} hover:opacity-90 text-white py-6 text-lg rounded-xl`}>
                      Start Your Journey
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* AI Tools Section */}
              <div className="mt-6">
                <h3 className="text-lg font-heading font-bold text-navy-700 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-500" />
                  AI-Powered Tools
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* AI Financial Coach - Special card that opens chat */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card 
                      className="h-full hover:shadow-md transition-all cursor-pointer border border-emerald-200 hover:border-emerald-400 bg-emerald-50"
                      onClick={() => setAiCoachOpen(true)}
                      data-testid="ai-coach-card"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
                            <Brain className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-navy-700 text-sm">AI Financial Coach</h4>
                            <p className="text-xs text-slate-500 mt-0.5">Get personalized advice from our AI assistant</p>
                            <Badge className="mt-2 bg-emerald-100 text-emerald-700 text-xs">Click to chat</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  {/* Other AI Tools */}
                  {AI_TOOLS.map((tool, index) => (
                    <motion.div
                      key={tool.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                    >
                      <Link to={tool.path}>
                        <Card className="h-full hover:shadow-md transition-all cursor-pointer border border-slate-200 hover:border-emerald-300">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 ${tool.color} rounded-lg flex items-center justify-center shrink-0`}>
                                <tool.icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-navy-700 text-sm">{tool.name}</h4>
                                <p className="text-xs text-slate-500 mt-0.5">{tool.description}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Wealthsimple $25 Offer */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6"
                >
                  <Card className="border-0 shadow-md bg-gradient-to-r from-emerald-500 to-emerald-600 text-white overflow-hidden">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                          <Gift className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading font-bold text-lg">Get free $25 towards your Wealthsimple TFSA account</h4>
                          <p className="text-emerald-100 text-sm">Open a Wealthsimple TFSA and start your investing journey</p>
                        </div>
                        <Button
                          onClick={() => setWealthsimpleModalOpen(true)}
                          className="bg-white text-emerald-600 hover:bg-emerald-50 shrink-0"
                          data-testid="dashboard-wealthsimple-btn"
                        >
                          Claim Free $25 →
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Column - Suggested Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Target className="w-5 h-5 text-slate-500" />
                    Next Steps
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    After mastering your primary category, explore these:
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {suggestedCategories.map((categoryKey, index) => {
                    const cat = CATEGORY_INFO[categoryKey];
                    if (!cat) return null;
                    const CatIcon = cat.icon;
                    return (
                      <Link key={categoryKey} to={cat.path}>
                        <div className={`p-4 rounded-xl ${cat.bgColor} hover:opacity-80 transition-all cursor-pointer`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 bg-gradient-to-r ${cat.color} rounded-lg flex items-center justify-center`}>
                              <CatIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className={`font-semibold ${cat.textColor} text-sm`}>{cat.name}</p>
                              <p className="text-xs text-slate-600">{cat.description}</p>
                            </div>
                            <ChevronRight className={`w-5 h-5 ${cat.textColor}`} />
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  {/* Learn Hub Link */}
                  <Link to="/tools?tool=learnhub">
                    <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 hover:opacity-80 transition-all cursor-pointer mt-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-indigo-600 text-sm">Learn Hub</p>
                          <p className="text-xs text-slate-600">Interactive financial lessons</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-indigo-500" />
                      </div>
                    </div>
                  </Link>

                  {/* Stock Simulator Link */}
                  <Link to="/tools?tool=simulator">
                    <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 hover:opacity-80 transition-all cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-emerald-600 text-sm">Stock Simulator</p>
                          <p className="text-xs text-slate-600">Practice with virtual money</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-emerald-500" />
                      </div>
                    </div>
                  </Link>
                </CardContent>
              </Card>

              {/* Quick Links - Always visible */}
              <Card className="border-0 shadow-sm mt-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Quick Links</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Link to="/tools" className="flex items-center gap-3 p-3 rounded-lg hover:bg-emerald-50 hover:border-emerald-200 border border-transparent transition-colors">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Wrench className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-700">All Tools</span>
                        <p className="text-xs text-slate-500">Calculators, simulators & more</p>
                      </div>
                    </Link>
                    <Link to="/blog" className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Newspaper className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-700">Blog & Guides</span>
                        <p className="text-xs text-slate-500">Articles & beginner guides</p>
                      </div>
                    </Link>
                    <Link to="/seminars" className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 hover:border-purple-200 border border-transparent transition-colors">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-700">Free Seminars</span>
                        <p className="text-xs text-slate-500">Live workshops & events</p>
                      </div>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
      
      {/* Wealthsimple Steps Modal */}
      <WealthsimpleStepsModal 
        open={wealthsimpleModalOpen} 
        onOpenChange={setWealthsimpleModalOpen} 
      />
      
      {/* AI Financial Coach - Always available */}
      <AIFinancialCoach 
        investorType={investorData?.name}
        recommendedCategory={recommendedCategory}
        userName={user?.name?.split(' ')[0]}
        isOpen={aiCoachOpen}
        onOpenChange={setAiCoachOpen}
      />
    </SignInOverlay>
  );
};

export default Dashboard;
